/**
 * Knows how to map TunerVars from server into the proper substate.
 * Used by ConfigurationStoreEnhancer to set the initial store data from the configuration.
 * Ported from SavageBeast/Engineering/projects/radio/src/js/shared/TunerVars.js
 */
import { Map } from 'immutable';
import get from 'lodash/get';
import isBoolean from 'lodash/isBoolean';
import isString from 'lodash/isString';

import { CountryCodes } from '../../constants/CountryCodes';
import { ChronosTestModeValues, TunerVarStateKeys } from '../../constants/TunerVarStateKeys';
import { getSearchPathForTermAndType } from '../../lib/RouteUtils';

import { getRawTunerVarsData, stringToInt } from './TunerVarUtils';

const SEARCH_FILTER_VALUE_TRACK = 'track';
const SEARCH_FILTER_VALUE_SONG = 'song';
const POLL_COLLECTION_INTERVAL_MS = 300000; // five minutes
const DEFAULT_ANTI_AD_BLOCK_MUSIC_TIMEOUT = 840000; // fourteen minutes
const DEFAULT_AD_BLOCK_CHECK_INTERVAL = 900000; // fifteen minutes
/**
 * Converts a string into a boolean value, using a default value if string is null
 *
 * @param {string} value - input
 * @param {boolean} defaultValue value to return if input is null, or not a string
 * @returns {boolean} value as boolean
 */
function getBooleanValue(value, defaultValue) {
  if (isBoolean(value)) {
    return value;
  }

  if (value === null || !isString(value)) {
    return defaultValue;
  }
  return value.toLowerCase() === 'true';
}

/**
 * Station code of station to auto-create (or play if already exists) after user authenticates
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string | null} station code
 */
function getStationCode(tunerVarData) {
  const stationCode = tunerVarData.sc;
  if (!stationCode && !isString(stationCode)) {
    return null;
  }
  // ignore "stationCode=sh" (with no actual id) which is invalid format but might be out in the wild (WEB-2907)
  return stationCode !== 'sh' && stationCode.length > 0 ? stationCode : null;
}

/**
 * Search data from server
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string | null} search data
 */
function getSearch(tunerVarData) {
  return tunerVarData.search || null;
}

/**
 * Get a search filter to accompany a search.  Can be 'artist or 'song'
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string | null} search filter
 */
function getSearchFilter(tunerVarData) {
  let val = tunerVarData.searchfilter;
  if (isString(val)) {
    val = val.toLowerCase();
    // It can sometimes be passed in as "track" instead of "song", so convert it (fixes WEB-2815)
    return val === SEARCH_FILTER_VALUE_TRACK ? SEARCH_FILTER_VALUE_SONG : val;
  }
  return null;
}

/**
 * Returns the search to execute determined by the env loading the application
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string | null} search redirect path
 */
function getSearchRedirect(tunerVarData) {
  const searchTerm = getSearch(tunerVarData);
  if (!searchTerm) {
    return null;
  }
  return getSearchPathForTermAndType(searchTerm, getSearchFilter(tunerVarData));
}

/**
 * Number of seconds to wait before a stream limit nag is presented.
 * (This value may only be reduced via tuner vars. It may not be increased.)
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {number | null} timeout
 */
function getStreamNagTimeout(tunerVarData) {
  // NOTE: this is further derived in radio: ListenerData.prototype.getInactivityTimeout
  return stringToInt(tunerVarData.streamNagTimeout) * 1000 || null;
}

/**
 * Max number of seconds of playtime allowed for an anonymous user.
 * (This value may only be reduced via tuner vars. It cannot not be increased.)
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {number} playtime
 */
function getPlaytime(tunerVarData) {
  // NOTE: this is further derived in radio: ListenerData.prototype.hasRemainingPlayTime
  return stringToInt(tunerVarData.playtime) || null;
}

/**
 * Auth token that allows CS to log in as a user
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string | null} token
 */
function getCustomerServiceAuthToken(tunerVarData) {
  return tunerVarData.csat || null;
}

/**
 * Auth token that goes straight to password reset page
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string | undefined} token
 */
function getResetPasswordAuthToken(tunerVarData) {
  return tunerVarData.pt || null;
}

/**
 * Passed in offer code (deletes after reading)
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} code
 */
function getOfferCode(tunerVarData) {
  return tunerVarData.code || null;
}

/**
 * Flag indicating when we want to disable auto-play of last station (i.e. if user comes to backstage page via P1 client)
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {boolean} is autoplay disabled
 */
function getDisableAutoplay(tunerVarData) {
  const stationCode = getStationCode(tunerVarData);
  const hasStationCode = !!(stationCode && stationCode.length > 0);
  return tunerVarData.autoplay === 0 || hasStationCode;
}

/**
 * Hostname that served the client
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} hostname
 */
function getHostname(tunerVarData) {
  return tunerVarData.host;
}

/**
 * Port that served the client.
 * The value being read is `sport`, the tuner var includes port and sport (secure).
 * We only ever want to access via secure port.
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} port
 */
function getPort(tunerVarData) {
  return stringToInt(tunerVarData.sport);
}

function getStatsJsonApiUrl(tunerVarData) {
  return tunerVarData.statsJsonApiUrl;
}

function getStaticImageUrl(tunerVarData) {
  return (tunerVarData.staticImageUrl && `${tunerVarData.staticImageUrl}/`) || null;
}

function getDynamicImageUrl(tunerVarData) {
  return (tunerVarData.dynamicImageUrl && `${tunerVarData.dynamicImageUrl}/`) || null;
}

function getDynamicImageSizes(tunerVarData) {
  const sizes = tunerVarData.dynamicImageSizes || '';
  return sizes.split(',');
}

/**
 * Max delay in milliseconds before sending events
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {number} interval
 */
function getStatsInterval(tunerVarData) {
  return tunerVarData.statsInterval;
}

/**
 * Max number of events that can be sent in a single HTTP request
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {number} batch size
 */
function getStatsBatchSize(tunerVarData) {
  return tunerVarData.statsBatchSize;
}

function getVideoStartTimeoutMs(tunerVarData) {
  return tunerVarData.videoStartTimeoutMs;
}

/**
 * Hostname of the url shortening service
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} host
 */
function getProxyHost(tunerVarData) {
  return tunerVarData.proxyHost;
}

/**
 * Port of the url shortening service
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {number} port
 */
function getProxyPort(tunerVarData) {
  return tunerVarData.proxyPort;
}

/**
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} home page url
 */
function getHomePageUrl(tunerVarData) {
  const secureHostname = getHostname(tunerVarData);
  const securePort = getPort(tunerVarData);
  return `https://${secureHostname}:${securePort}/`;
}

/**
 * 2 character Country Code of the listener based on IP Address lookup
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} country code
 */
function getCountryCode(tunerVarData) {
  return tunerVarData.countryCode || CountryCodes.US;
}

/**
 * Can we display Samples
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {boolean} can display samples
 */
function canDisplaySamples(tunerVarData) {
  return getCountryCode(tunerVarData) === CountryCodes.US;
}

/**
 * the regular expression used to validate the postal code
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} regexp
 */
function getPostalCodeValidationRegex(tunerVarData) {
  if (!tunerVarData.postalCodeValidationRegex) {
    return '^\\d{5}$';
  }
  return tunerVarData.postalCodeValidationRegex;
}

/**
 * Request for test artist message in playlist fragments
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} test message. Format is "<index>:<messageId>"
 *    where index is position in fragment and messageId is:
 *      1 : deliveryType AFTER_TRACK
 *      2-5 : deliveryType ANYTIME (Checkout RadioArtistMessageService::getTestMessage for additional characteristics)
 *      6 : deliveryType BEFORE_TRACK
 *   so: "1:1:2:1" means the getFragment (assuming 4 tracks per fragment) will be "Track,Test AAM 1,Track,Test AAM 1,Track,Track"
 */
function getTestArtistMessage(tunerVarData) {
  return tunerVarData.testArtistMessage || '';
}

function getTestVoiceTrack(tunerVarData) {
  return tunerVarData.testVoiceTrack || '';
}

/**
 * Get any artist message data that should be passed on getFragment
 * call on station start
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string | null}
 */
function getArtistMessage(tunerVarData) {
  return tunerVarData.hxam || null;
}

/**
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {boolean} boolean
 */
function isAAMFlaggingV2(tunerVarData) {
  return tunerVarData.isAAMFlaggingV2 === true;
}

/**
 * Is server runtime config for allowing opt out enabled?
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {boolean} boolean
 */
function getOptOutAllowed(tunerVarData) {
  return tunerVarData.allowOptOut === true;
}

/**
 * Is server runtime config for alerting client version mismatch enabled?
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {boolean} boolean
 */
function getShouldAlertClientVersionMismatch(tunerVarData) {
  return tunerVarData.shouldAlertOnClientVersionMismatch === true;
}

/**
 * Facebook ref string that may be passed in as a URL parameter from Facebook links
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} facebook ref
 */
function getFacebookRef(tunerVarData) {
  return tunerVarData.fb_ref || null;
}

/**
 * Facebook share App Id
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string | null} facebook share id
 */
function getFacebookShareAppId(tunerVarData) {
  return tunerVarData.facebookShareAppId || null;
}

/**
 * Comma-separated list of FB permissions that we ask for when user Facebook connects
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} permissions
 */
function getFacebookConnectPermissions(tunerVarData) {
  return tunerVarData.facebookConnectPermissions || null;
}

/**
 * Facebook sharing base URL - to get around firewall issues, facebook track sharing can be overridden to a public url.
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} url
 */
function getFacebookTrackShareBaseUrl(tunerVarData) {
  const path = tunerVarData.facebookTrackShareBaseUrl;

  return path ? path : getHomePageUrl(tunerVarData);
}

/**
 * Twitter oauth consumer key
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} consumer key
 */

function getTwitterShareConsumerKey(tunerVarData) {
  return tunerVarData.twitterShareConsumerKey || null;
}

/**
 * Twitter oauth consumer secret
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} consumer secret
 */

function getTwitterShareConsumerSecret(tunerVarData) {
  return tunerVarData.twitterShareConsumerSecret || null;
}

/**
 * Whether or not to "force Flash" even for browsers that support HTML5 audio
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {boolean} true if the force Flash code is to be used
 */
function getForceFlash(tunerVarData) {
  return getBooleanValue(tunerVarData.forceFlash, false);
}

/**
 * Whether or not to show an order confirmation screen in tuner when listener returns from PayPal site.
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {boolean} true if order confirmation screen should be shown
 */
function isPaypalOrderConfirmScreenRequired(tunerVarData) {
  return getBooleanValue(tunerVarData.paypalShowOrderConfirmScreen, false);
}

/** ******************************* Ad Related */

/**
 * Display first impression from share sponsorship
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {*} share imp
 */
function getShareImp(tunerVarData) {
  return tunerVarData.shareImp || null;
}

/**
 * Id of a display ad to force
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} display ad id
 */
function getDisplayAd(tunerVarData) {
  return tunerVarData.displayAd || null;
}

/**
 * Id of a companion display ad to force (used in conjunction with "displayAd" when an ad has a master/companion relationship)
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} companion ad id
 */
function getCompanionAd(tunerVarData) {
  return tunerVarData.companionAd || null;
}

const TEST_AD_SEQUENCE = [
  '246976901:44334262', // Masthead with companion
  '215333213:33803897', // Network ad
  '246976901:44334262', // Masthead with companion
  '210400547:43579993', // 300px banner (house banner)
  '246976901:44334262', // Masthead with companion
  '246983691:44307832', // Masthead w/o companion
  '215333213:33803897', // Network ad
  '246983691:44307832', // Masthead w/o companion
  '210400547:43579993', // 300px banner (house banner)
  '246983691:44307832', // Masthead w/o companion
  '246976901:44334262', // Masthead with companion
  '215333213:33803897', // Network ad
  '210400547:43579993', // 300px banner (house banner)
  '215333213:33803897', // Network ad
];

/**
 * When true, indicates that we want to go through a specific sequence of test ads (currently hardcoded)
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string[]} ad sequence
 */
function getTestAdSequence(tunerVarData) {
  if (getBooleanValue(tunerVarData.testAdSequence, false)) {
    return TEST_AD_SEQUENCE;
  }
  return [];
}

/**
 * Id of an audio (aka playlist) ad to force
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} audio ad id
 */
function getAudioAd(tunerVarData) {
  return tunerVarData.audioAd || null;
}

/**
 * Name of a video ad to play on skip interaction (RADIO-9609)
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} video ad id
 */
function getVideoAd(tunerVarData) {
  return tunerVarData.videoAd || null;
}

/**
 * Id of a promoted station to force
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} promoted station id
 */
function getPromotedStation(tunerVarData) {
  return tunerVarData.promotedStation || null;
}

/**
 * The publisher to use when requesting ads from DART
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} ad publisher
 */
function getAdPublisher(tunerVarData) {
  return tunerVarData.pub || null;
}

/**
 * The zone to use when requesting ads from DART
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} ad zone
 */
function getAdZone(tunerVarData) {
  return tunerVarData.zone || null;
}

/**
 * The organization to use when requesting ads from DART
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} organization
 */
function getAdOrganization(tunerVarData) {
  return tunerVarData.organization || null;
}

/**
 * maxAdInitiatedRefreshDelaySeconds
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {number} refresh delay
 */
function getMaxAdInitiatedRefreshDelaySeconds(tunerVarData) {
  const maxAdInitiatedRefreshDelaySeconds = tunerVarData.maxAdInitiatedRefreshDelaySeconds;
  return maxAdInitiatedRefreshDelaySeconds ? maxAdInitiatedRefreshDelaySeconds : 60;
}

/**
 * Are we in ad test mode? When enabled, this turns on
 * audio and video ad modes
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {boolean} is ad test mode
 */
function getAdTestMode(tunerVarData) {
  return getBooleanValue(tunerVarData.adTestMode, false);
}

/**
 * Should we use canned ads
 * This value is pulled from query params by the server & into TunerVars
 * https://VM/?useTestAds=true
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {boolean} use test ads
 */
function getUseTestAds(tunerVarData) {
  return getBooleanValue(tunerVarData.useTestAds, false);
}

/**
 * Are we in audio ad test mode?
 * This value is pulled from query params by the server & into TunerVars
 * https://VM/?audioAdTestMode=true
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {boolean} audio ad test mode
 */
function getAudioAdTestMode(tunerVarData) {
  return getBooleanValue(tunerVarData.audioAdTestMode, false);
}

/**
 * Are we in Audibility test mode?
 * This value is pulled from query params by the server & into TunerVars
 * https://VM/?audibilityTestMode=true
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {boolean} audibility test mode
 */
function getAudibilityTestMode(tunerVarData) {
  return getBooleanValue(tunerVarData.audibilityTestMode, false);
}

/**
 * Are we in video ad test mode?
 * This value is pulled from query params by the server & into TunerVars
 * https://VM/?videoAdTestMode=true
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {boolean} video ad test mode
 */
function getVideoAdTestMode(tunerVarData) {
  return getBooleanValue(tunerVarData.videoAdTestMode, false);
}

/**
 * Determine the ad test mode for chronos
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} chronos test mode
 */
function getChronosTestMode(tunerVarData) {
  const audio =
    getAudioAd(tunerVarData) !== null || // This forces a specific audio ad
    getAdTestMode(tunerVarData) || // This enables test mode for all ad types
    getAudioAdTestMode(tunerVarData); // Test mode for audio ads  only

  const video =
    getVideoAd(tunerVarData) !== null || // Force a specific video
    getAdTestMode(tunerVarData) || // Test mode for all ad types
    getVideoAdTestMode(tunerVarData); // Test mode for video ads  only

  if (audio && video) {
    return ChronosTestModeValues.CHRONOS_TEST_MODE_VALUE_ALL;
  } else if (audio) {
    return ChronosTestModeValues.CHRONOS_TEST_MODE_VALUE_AUDIO;
  } else if (video) {
    return ChronosTestModeValues.CHRONOS_TEST_MODE_VALUE_VIDEO;
  }
  return ChronosTestModeValues.CHRONOS_TEST_MODE_VALUE_NONE;
}

/**
 * Are video ads enabled
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {boolean} video ad enabled
 */
function isVideoAdsEnabled(tunerVarData) {
  return getBooleanValue(tunerVarData.apvEnabled, false);
}

const DEFAULT_DFP_NETWORK_CODE = '4204';

/**
 * The doubleclick for publishers Code - used by Simple URLs in AdService.js (see PYM-1153)
 *
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {string} code
 */
function getDfpNetworkCode(tunerVarData) {
  return tunerVarData.dfpNetworkCode || DEFAULT_DFP_NETWORK_CODE;
}

/**
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {number} refresh interval
 */
function getAdRefreshInterval(tunerVarData) {
  return tunerVarData.adRefreshInterval;
}

/**
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {number} interval
 */
function getIntervalBetweenVideoAndDisplayAd(tunerVarData) {
  return tunerVarData.timeBetweenVideoAndDisplayAd;
}

/**
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {number} interval
 */
function getIntervalBetweenDisplayAds(tunerVarData) {
  return tunerVarData.timeBetweenDisplayAds;
}

/**
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {number} interval
 */
function getIntervalBetweenAudioAndDisplayAd(tunerVarData) {
  return tunerVarData.timeBetweenAudioAndDisplayAd;
}

/**
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {number} interval
 */
function getIntervalBetweenDisplayAndAudioAd(tunerVarData) {
  return tunerVarData.timeBetweenDisplayAndAudioAd;
}

function getPaidHostname(tunerVarData) {
  return tunerVarData.paidHostname;
}

/**
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {object} audio player browser support
 */
function getAudioPlayerSupportedBrowsers(tunerVarData) {
  return tunerVarData.audioPlayerSupportedBrowsers || new Map();
}

function getSupportedBrowsers(tunerVarData) {
  return tunerVarData.browserSupport || [];
}

function getReplayableTrackHistoryCount(tunerVarData) {
  return tunerVarData.flexReplayableTrackHistoryCount || 30;
}

function getValueExchangeVideoProgressMaxBufferingSecs(tunerVarData) {
  return tunerVarData.vxVideoProgressMaxBufferingSecs || 8;
}

function getWebMonitoringLogLevel(tunerVarData) {
  return tunerVarData.webMonitoringLogLevel;
}

function isWebMonitoringEnabled(tunerVarData) {
  return tunerVarData.webMonitoringEnabled;
}

function getTrackJsAppName(tunerVarData) {
  return tunerVarData.trackJsAppName;
}

function getWebMonitoringAppName(tunerVarData) {
  return tunerVarData.webMonitoringAppName;
}

function getEnableStatsTestLogger(tunerVarData) {
  return tunerVarData.enableStatsTestLogger;
}

function getEnablePremium(tunerVarData) {
  return tunerVarData.featureFlags && tunerVarData.featureFlags.premium;
}

function getEnableUserProfile2(tunerVarData) {
  return tunerVarData.featureFlags && tunerVarData.featureFlags.Profile2Enabled ? true : false;
}

function getAutomaticPlaylistEnabled(tunerVarData) {
  return tunerVarData.featureFlags && tunerVarData.featureFlags.enableAutomaticPlaylist
    ? true
    : false;
}

function getPaContentIdTargetingEnabled(tunerVarData) {
  return tunerVarData.featureFlags && tunerVarData.featureFlags.enablePaContentIdTargeting
    ? true
    : false;
}

function getMegastarModesEnabled(tunerVarData) {
  return tunerVarData.featureFlags && tunerVarData.featureFlags.megastarModesEnabled ? true : false;
}

function getSharedPlayerStateGraphQLEnabled(tunerVarData) {
  return tunerVarData.featureFlags && tunerVarData.featureFlags.sharedPlayerStateGraphQLEnabled
    ? true
    : false;
}

function getPollCollectionIntervalInMilliseconds(tunerVarData) {
  if (tunerVarData.pollCollectionInterval && tunerVarData.pollCollectionInterval >= 0) {
    return tunerVarData.pollCollectionInterval;
  }
  return POLL_COLLECTION_INTERVAL_MS; // 5 min is default
}

function getInitialPandoraId(tunerVarData) {
  return tunerVarData.currentPandoraId || null;
}

function getDisableThirdPartyTrackers(tunerVarData) {
  return tunerVarData.disableThirdPartyBeacons || null;
}

function getSheerIdClientApiBaseUrl(tunerVarData) {
  return tunerVarData.sheerIdClientApiBaseUrl || null;
}

function getAmpUrls(tunerVarData) {
  return tunerVarData.ampUrls || {};
}

function getWebPandoraIdsWithDoubleSlashUri(tunerVarData) {
  return tunerVarData.webPandoraIdsWithDoubleSlashUri || '';
}

function getDeepLinkingUrl(tunerVarData) {
  return tunerVarData.deepLinkingUrl || '';
}

function getIsOnDemandFamilyPlanAcceptInviteEnabled(tunerVarData) {
  return get(tunerVarData, 'featureFlags.onDemandFpAcceptInviteEnabled', false);
}

function getIsOnDemandSpecialOffersEnabled(tunerVarData) {
  return get(tunerVarData, 'featureFlags.onDemandSpecialOffersEnabled', false);
}

function getBlackhawkEnabled(tunerVarData) {
  return tunerVarData.featureFlags && tunerVarData.featureFlags.blackhawkEnabled ? true : false;
}

function getCCPALinkEnabled(tunerVarData) {
  return tunerVarData.webCCPALinkEnabled || false;
}

function getCCPALinkUrl(tunerVarData) {
  return tunerVarData.webCCPALinkUrl || '';
}

function getPremiumAccessStartToneUrl(tunerVarData) {
  return tunerVarData.vxPremiumAccessLeadInAudioToneUrl || '';
}

function getPremiumAccessEndToneUrl(tunerVarData) {
  return tunerVarData.vxPremiumAccessLeavePremiumToneAudioUrl || '';
}

function getPremiumAccessWelcomeMessageUrl(tunerVarData) {
  return tunerVarData.vxPremiumAccessLeadInAudioMessageUrl || '';
}

function getPremiumAccessWelcomeMessageLimit(tunerVarData) {
  return tunerVarData.vxPremiumAccessLeadInAudioMessageLimit || 1;
}

function getAntiAdBlockerMusicTimeout(tunerVarData) {
  return tunerVarData.webAntiAdBlockerMusicTimeout >= 0
    ? tunerVarData.webAntiAdBlockerMusicTimeout
    : DEFAULT_ANTI_AD_BLOCK_MUSIC_TIMEOUT;
}

function getAdBlockerCheckInterval(tunerVarData) {
  return tunerVarData.webAdBlockerCheckInterval >= 0
    ? tunerVarData.webAdBlockerCheckInterval
    : DEFAULT_AD_BLOCK_CHECK_INTERVAL;
}

function getResponsiveSubscriptionEnabled(tunerVarData) {
  return tunerVarData.featureFlags && tunerVarData.featureFlags.responsiveSubscriptionEnabled
    ? true
    : false;
}

/**
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {boolean} non-binary gender option
 */
function getNonBinaryGenderEnabled(tunerVarData) {
  return tunerVarData.featureFlags && tunerVarData.featureFlags.nonBinaryGenderEnabled
    ? true
    : false;
}

/**
 * @param {object} tunerVarData - key/values sent by server on app load
 * @returns {boolean} is desktop deep linking enabled
 */
function getDesktopDeepLinkingEnabled(tunerVarData) {
  return tunerVarData.featureFlags && tunerVarData.featureFlags.desktopDeepLinkingEnabled
    ? true
    : false;
}

function getArtistBackstageUrqlEnabled(tunerVarData) {
  return tunerVarData.featureFlags && tunerVarData.featureFlags.artistBackstageUrqlEnabled
    ? true
    : false;
}

function getLogOutOfAllDevicesEnabled(tunerVarData) {
  return tunerVarData.featureFlags && tunerVarData.featureFlags.logOutOfAllDevicesEnabled
    ? true
    : false;
}

function getDeviceUuidEnabled(tunerVarData) {
  return tunerVarData.featureFlags && tunerVarData.featureFlags.deviceUuidEnabled ? true : false;
}

function getCollectionV7EndpointsEnabled(tunerVarData) {
  return !!tunerVarData?.featureFlags?.collectionV7EndpointsEnabled;
}

function getAudioPlayerSDKEnabled(tunerVarData) {
  return !!tunerVarData?.featureFlags?.audioPlayerSDKEnabled;
}

function getCuratorExperienceEnabled(tunerVarData) {
  return !!tunerVarData?.featureFlags?.curatorExperienceEnabled;
}

function getGraphQLMigrationEnabled(tunerVarData) {
  return !!tunerVarData?.featureFlags?.graphQLMigrationEnabled;
}

function getFilterT1UsersEnabled(tunerVarData) {
  return !!tunerVarData?.featureFlags?.filterT1UsersEnabled;
}

function getFallbackADKVDeprecationEnabled(tunerVarData) {
  return !!tunerVarData?.featureFlags?.fallbackADKVDeprecationEnabled;
}

function getIsPrivacyLawComplianceEnabled(tunerVarData) {
  return !!tunerVarData?.featureFlags?.privacyLawComplianceEnabled;
}

/**
 * Mapping function knows how to map TunerVar data into `tunerVars` substate
 *
 * @param {object} rawTunerVars - the config data provided by the server
 * @returns {object} - receives the Redux store state and enhances its substate
 */
export function mapTunerVarsToSubstate(rawTunerVars = {}) {
  return {
    /* App related vars */
    [TunerVarStateKeys.SEARCH_REDIRECT_ON_STARTUP]: getSearchRedirect(rawTunerVars),
    [TunerVarStateKeys.STREAMING_INACTIVITY_TIMEOUT_MS]: getStreamNagTimeout(rawTunerVars),
    [TunerVarStateKeys.MAX_NUM_ANON_PLAYTIME_SECONDS]: getPlaytime(rawTunerVars),
    [TunerVarStateKeys.CS_AUTH_TOKEN]: getCustomerServiceAuthToken(rawTunerVars),
    [TunerVarStateKeys.PASSWORD_RESET_TOKEN]: getResetPasswordAuthToken(rawTunerVars),
    [TunerVarStateKeys.OFFER_CODE]: getOfferCode(rawTunerVars),
    [TunerVarStateKeys.AUTOPLAY_ON_STARTUP_DISABLED]: getDisableAutoplay(rawTunerVars),
    [TunerVarStateKeys.APPLICATION_HOSTNAME]: getHostname(rawTunerVars),
    [TunerVarStateKeys.APPLICATION_PORT]: getPort(rawTunerVars),
    [TunerVarStateKeys.STATS_JSON_API_URL]: getStatsJsonApiUrl(rawTunerVars),
    [TunerVarStateKeys.STATS_COLLECTION_INTERVAL_MS]: getStatsInterval(rawTunerVars),
    [TunerVarStateKeys.STATS_COLLECTION_BATCH_SIZE]: getStatsBatchSize(rawTunerVars),
    [TunerVarStateKeys.URL_SHORTENING_SERVICE_HOST]: getProxyHost(rawTunerVars),
    [TunerVarStateKeys.URL_SHORTENING_SERVICE_PORT]: getProxyPort(rawTunerVars),
    [TunerVarStateKeys.FORCE_FLASH]: getForceFlash(rawTunerVars),
    [TunerVarStateKeys.REQUIRE_PAYPAL_CONFIRMATION]:
      isPaypalOrderConfirmScreenRequired(rawTunerVars),
    [TunerVarStateKeys.COUNTRY_CODE]: getCountryCode(rawTunerVars),
    [TunerVarStateKeys.CAN_DISPLAY_SAMPLES]: canDisplaySamples(rawTunerVars),
    [TunerVarStateKeys.POSTAL_CODE_VALIDATION_REGEXP]: getPostalCodeValidationRegex(rawTunerVars),
    [TunerVarStateKeys.TEST_ARTIST_MESSAGE]: getTestArtistMessage(rawTunerVars),
    [TunerVarStateKeys.TEST_VOICE_TRACK]: getTestVoiceTrack(rawTunerVars),
    [TunerVarStateKeys.ON_DEMAND_ARTIST_MESSAGE_DATA]: getArtistMessage(rawTunerVars),
    [TunerVarStateKeys.IS_AAMF_FLAGGING_V2]: isAAMFlaggingV2(rawTunerVars),
    [TunerVarStateKeys.AUDIO_PLAYER_SUPPORTED_BROWSERS]:
      getAudioPlayerSupportedBrowsers(rawTunerVars),
    [TunerVarStateKeys.SUPPORTED_BROWSERS]: getSupportedBrowsers(rawTunerVars),
    [TunerVarStateKeys.WEB_2016_CLIENT_OPT_OUT_ALLOWED]: getOptOutAllowed(rawTunerVars),
    [TunerVarStateKeys.WEB_ALERT_ON_CLIENT_VERSION_MISMATCH]:
      getShouldAlertClientVersionMismatch(rawTunerVars),

    /* FB vars */
    [TunerVarStateKeys.FACEBOOK_REF_TOKEN]: getFacebookRef(rawTunerVars),
    [TunerVarStateKeys.FACEBOOK_SHARE_APP_ID]: getFacebookShareAppId(rawTunerVars),
    [TunerVarStateKeys.FACEBOOK_CONNECT_PERMISSIONS]: getFacebookConnectPermissions(rawTunerVars),
    [TunerVarStateKeys.FACEBOOK_TRACK_SHARE_BASE_URL]: getFacebookTrackShareBaseUrl(rawTunerVars),

    /* Twitter vars */
    [TunerVarStateKeys.TWITTER_SHARE_CONSUMER_KEY]: getTwitterShareConsumerKey(rawTunerVars),
    [TunerVarStateKeys.TWITTER_SHARE_CONSUMER_SECRET]: getTwitterShareConsumerSecret(rawTunerVars),

    /* Ad related vars */
    [TunerVarStateKeys.SHARE_FIRST_IMPRESSION]: getShareImp(rawTunerVars),
    [TunerVarStateKeys.FORCED_DISPLAY_AD_ID]: getDisplayAd(rawTunerVars),
    [TunerVarStateKeys.FORCED_COMPANION_AD_ID]: getCompanionAd(rawTunerVars),
    [TunerVarStateKeys.TEST_AD_SEQUENCE]: getTestAdSequence(rawTunerVars),
    [TunerVarStateKeys.FORCED_AUDIO_AD_ID]: getAudioAd(rawTunerVars),
    [TunerVarStateKeys.TEST_VIDEO_AD_ID]: getVideoAd(rawTunerVars),
    [TunerVarStateKeys.PROMOTED_STATION_ID]: getPromotedStation(rawTunerVars),
    [TunerVarStateKeys.AD_PUBLISHER]: getAdPublisher(rawTunerVars),
    [TunerVarStateKeys.AD_ZONE]: getAdZone(rawTunerVars),
    [TunerVarStateKeys.AD_ORGANIZATION]: getAdOrganization(rawTunerVars),
    [TunerVarStateKeys.MAX_AD_INITIATED_REFRESH_DELAY]:
      getMaxAdInitiatedRefreshDelaySeconds(rawTunerVars),
    [TunerVarStateKeys.IS_AD_TEST_MODE]: getAdTestMode(rawTunerVars),
    [TunerVarStateKeys.USE_TEST_ADS]: getUseTestAds(rawTunerVars),
    [TunerVarStateKeys.AUDIO_AD_TEST_MODE]: getAudioAdTestMode(rawTunerVars),
    [TunerVarStateKeys.VIDEO_AD_TEST_MODE]: getVideoAdTestMode(rawTunerVars),
    [TunerVarStateKeys.CHRONOS_TEST_MODE]: getChronosTestMode(rawTunerVars),
    [TunerVarStateKeys.ARE_VIDEO_ADS_ENABLED]: isVideoAdsEnabled(rawTunerVars),
    [TunerVarStateKeys.DFP_NETWORK_CODE]: getDfpNetworkCode(rawTunerVars),
    [TunerVarStateKeys.AD_REFRESH_INTERVAL]: getAdRefreshInterval(rawTunerVars),
    [TunerVarStateKeys.VIDEO_START_TIMEOUT_MS]: getVideoStartTimeoutMs(rawTunerVars),
    [TunerVarStateKeys.INTERVAL_BETWEEN_VIDEO_AND_DISPLAY_AD]:
      getIntervalBetweenVideoAndDisplayAd(rawTunerVars),
    [TunerVarStateKeys.INTERVAL_BETWEEN_DISPLAY_ADS]: getIntervalBetweenDisplayAds(rawTunerVars),
    [TunerVarStateKeys.INTERVAL_BETWEEN_AUDIO_AND_DISPLAY_AD]:
      getIntervalBetweenAudioAndDisplayAd(rawTunerVars),
    [TunerVarStateKeys.INTERVAL_BETWEEN_DISPLAY_AND_AUDIO_AD]:
      getIntervalBetweenDisplayAndAudioAd(rawTunerVars),
    [TunerVarStateKeys.AUDIBILITY_TEST_MODE]: getAudibilityTestMode(rawTunerVars),
    [TunerVarStateKeys.DISABLE_THIRD_PARTY_BEACONS]: getDisableThirdPartyTrackers(rawTunerVars),

    /* replay related keys */
    [TunerVarStateKeys.REPLAYABLE_TRACK_HISTORY_COUNT]:
      getReplayableTrackHistoryCount(rawTunerVars),

    /* hostname (and port for DEV) for VIP that /billing endpoints should be using */
    [TunerVarStateKeys.PAID_HOSTNAME]: getPaidHostname(rawTunerVars),

    [TunerVarStateKeys.VALUE_EXCHANGE_VIDEO_PROGRESS_MAX_BUFFERING_SECS]:
      getValueExchangeVideoProgressMaxBufferingSecs(rawTunerVars),

    [TunerVarStateKeys.TRACKJS_APP_NAME]: getTrackJsAppName(rawTunerVars),
    [TunerVarStateKeys.IS_WEB_MONITORING_ENABLED]: isWebMonitoringEnabled(rawTunerVars),
    [TunerVarStateKeys.WEB_MONITORING_LOG_LEVEL]: getWebMonitoringLogLevel(rawTunerVars),
    [TunerVarStateKeys.WEB_MONITORING_APP_NAME]: getWebMonitoringAppName(rawTunerVars),

    [TunerVarStateKeys.ENABLE_STATS_TEST_LOGGER]: getEnableStatsTestLogger(rawTunerVars),
    [TunerVarStateKeys.ENABLE_PREMIUM]: getEnablePremium(rawTunerVars),
    [TunerVarStateKeys.ENABLE_USER_PROFILE_2]: getEnableUserProfile2(rawTunerVars),
    [TunerVarStateKeys.ENABLE_MEGASTAR_MODES]: getMegastarModesEnabled(rawTunerVars),
    [TunerVarStateKeys.ENABLE_SHARED_PLAYER_STATE_GRAPHQL]:
      getSharedPlayerStateGraphQLEnabled(rawTunerVars),
    [TunerVarStateKeys.ENABLE_AUTOMATIC_PLAYLIST_UNLOCK]: getAutomaticPlaylistEnabled(rawTunerVars),
    [TunerVarStateKeys.ENABLE_PA_CONTENT_ID_TARGETING]:
      getPaContentIdTargetingEnabled(rawTunerVars),
    [TunerVarStateKeys.WEB_CCPA_LINK_ENABLED]: getCCPALinkEnabled(rawTunerVars),
    [TunerVarStateKeys.ENABLE_ON_DEMAND_SPECIAL_OFFERS]:
      getIsOnDemandSpecialOffersEnabled(rawTunerVars),
    [TunerVarStateKeys.ENABLE_ON_DEMAND_FAMILY_PLAN_ACCEPT_INVITE]:
      getIsOnDemandFamilyPlanAcceptInviteEnabled(rawTunerVars),
    [TunerVarStateKeys.ENABLE_RESPONSIVE_SUBSCRIPTION]:
      getResponsiveSubscriptionEnabled(rawTunerVars),
    [TunerVarStateKeys.PRIVACY_LAW_COMPLIANCE_ENABLED]:
      getIsPrivacyLawComplianceEnabled(rawTunerVars),
    [TunerVarStateKeys.ENABLE_BLACKHAWK]: getBlackhawkEnabled(rawTunerVars),
    [TunerVarStateKeys.ENABLE_NON_BINARY_GENDER]: getNonBinaryGenderEnabled(rawTunerVars),
    [TunerVarStateKeys.ENABLE_DESKTOP_DEEP_LINKING]: getDesktopDeepLinkingEnabled(rawTunerVars),
    [TunerVarStateKeys.ENABLE_ARTIST_BACKSTAGE_URQL]: getArtistBackstageUrqlEnabled(rawTunerVars),
    [TunerVarStateKeys.ENABLE_LOG_OUT_OF_ALL_DEVICES]: getLogOutOfAllDevicesEnabled(rawTunerVars),
    [TunerVarStateKeys.ENABLE_DEVICE_UUID]: getDeviceUuidEnabled(rawTunerVars),
    [TunerVarStateKeys.ENABLE_COLLECTION_V7_ENDPOINTS]:
      getCollectionV7EndpointsEnabled(rawTunerVars),
    [TunerVarStateKeys.ENABLE_CURATOR_EXPERIENCE]: getCuratorExperienceEnabled(rawTunerVars),
    [TunerVarStateKeys.ENABLE_AUDIO_PLAYER_SDK]: getAudioPlayerSDKEnabled(rawTunerVars),
    [TunerVarStateKeys.ENABLE_GRAPHQL_MIGRATION]: getGraphQLMigrationEnabled(rawTunerVars),
    [TunerVarStateKeys.FILTER_T1_USERS_ENABLED]: getFilterT1UsersEnabled(rawTunerVars),

    [TunerVarStateKeys.STATIC_IMAGE_URL]: getStaticImageUrl(rawTunerVars),
    [TunerVarStateKeys.DYNAMIC_IMAGE_URL]: getDynamicImageUrl(rawTunerVars),
    [TunerVarStateKeys.DYNAMIC_IMAGE_SIZES]: getDynamicImageSizes(rawTunerVars),
    [TunerVarStateKeys.POLL_COLLECTION_INTERVAL]:
      getPollCollectionIntervalInMilliseconds(rawTunerVars),
    [TunerVarStateKeys.INITIAL_PANDORA_ID]: getInitialPandoraId(rawTunerVars),

    [TunerVarStateKeys.SHEER_ID_CLIENT_API_BASE_URL]: getSheerIdClientApiBaseUrl(rawTunerVars),
    [TunerVarStateKeys.AMP_URLS]: getAmpUrls(rawTunerVars),

    [TunerVarStateKeys.WEB_PANDORA_IDS_WITH_DOUBLE_SLASH_URI]:
      getWebPandoraIdsWithDoubleSlashUri(rawTunerVars),

    [TunerVarStateKeys.DEEP_LINKING_URL]: getDeepLinkingUrl(rawTunerVars),
    [TunerVarStateKeys.WEB_CCPA_LINK_URL]: getCCPALinkUrl(rawTunerVars),
    [TunerVarStateKeys.PREMIUM_ACCESS_START_TONE_URL]: getPremiumAccessStartToneUrl(rawTunerVars),
    [TunerVarStateKeys.PREMIUM_ACCESS_END_TONE_URL]: getPremiumAccessEndToneUrl(rawTunerVars),
    [TunerVarStateKeys.PREMIUM_ACCESS_WELCOME_MESSAGE_URL]:
      getPremiumAccessWelcomeMessageUrl(rawTunerVars),
    [TunerVarStateKeys.PREMIUM_ACCESS_WELCOME_MESSAGE_LIMIT]:
      getPremiumAccessWelcomeMessageLimit(rawTunerVars),

    [TunerVarStateKeys.WEB_ANTI_AD_BLOCKER_MUSIC_TIMEOUT]:
      getAntiAdBlockerMusicTimeout(rawTunerVars),
    [TunerVarStateKeys.WEB_AD_BLOCKER_CHECK_INTERVAL]: getAdBlockerCheckInterval(rawTunerVars),
    [TunerVarStateKeys.FALLBACK_ADKV_DEPRECATION_ENABLED]:
      getFallbackADKVDeprecationEnabled(rawTunerVars),
  };
}

export default function getTunerVarsInitialState() {
  return mapTunerVarsToSubstate(getRawTunerVarsData());
}
